import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch, apiPost, ApiError } from "../api";
import type { ID } from "../id";

// MembershipKind is the člen/nečlen distinction. It lives on the membership,
// not the person: the same human may be a registered member one season and a
// guest at a pohárová soutěž the next, and each season keeps its own truth.
//   registered — „Člen", a real season registration; may compete at MČR,
//                requires rodné číslo + address, follows the registration
//                window and the identity lock.
//   guest      — „Nečlen", a one-off entrant for pohárové soutěže; needs only
//                the identity and may be added/edited year-round.
/**
 * `foreign` („Zahraniční člen") is somebody on a team outside ČNSMS: the
 * same data and exemptions as a nečlen, but not the same thing — a nečlen
 * is a Czech club's one-off entrant, this is an ordinary member of a club
 * ČNSMS does not run. Which kinds a roster may hold follows the TEAM's
 * federation, never the person's citizenship.
 */
export type MembershipKind = "registered" | "guest" | "foreign";
export type MembershipRole = "coach" | "assistant" | "athlete";

export interface PersonPayload {
  first_name: string;
  last_name: string;
  country_code: string;
  birth_date: string;
  birth_number: string;
  street: string;
  city: string;
  postal_code: string;
  phone: string;
  // No e-mail: it lives on the User account only (see models.Person).
  // A person without a login has no address of their own.
}

// Person is the read shape returned by every endpoint that serves one
// (roster rows, the global registry list, a team's leader) — the backend's
// personDTO, which is embedded rather than re-declared per endpoint. It
// sends all fields unconditionally, so the PersonPayload strings are
// genuinely always present; do not mark them optional here.
export interface Person extends PersonPayload {
  id: ID;
  cmf_id: number | null;
  photo_uploaded: boolean;
}

export interface Membership {
  id: ID;
  team_id: ID;
  season_id: ID;
  season_code: string;
  person: Person;
  roles: MembershipRole[];
  kind: MembershipKind;
  member_since_year: number;
  valid_from: string;
  valid_to?: string | null;
  // The team this person is a registered člen of in the ACTIVE season, when
  // that is another team; "" otherwise. Filled on past-season lists only,
  // where it tells the transfer panel who has left (the server refuses to
  // transfer them).
  registered_elsewhere?: string;
}

interface ListResponse {
  data: Membership[];
  total: number;
}

export interface AddMemberPayload {
  person: PersonPayload;
  roles: MembershipRole[];
  kind: MembershipKind;
}

export interface UpdateMembershipPayload {
  roles: MembershipRole[];
  // Omit to leave the kind unchanged. Setting it to "registered" on a guest
  // row IS the act of registering that person into the season, and is what
  // makes the backend demand the full data set.
  kind?: MembershipKind;
  valid_to?: string | null;
}

// useTeamMembers lists a team's roster. Pass seasonId to view a specific
// (e.g. historical) season; omit it to view the active season. The season
// is part of the query key so switching seasons refetches cleanly.
export function useTeamMembers(teamId: ID | undefined, seasonId?: ID) {
  return useQuery<Membership[]>({
    queryKey: ["teams", teamId, "members", seasonId ?? "active"],
    enabled: !!teamId,
    queryFn: async () => {
      const url =
        seasonId !== undefined
          ? `/teams/${teamId}/members?season_id=${seasonId}`
          : `/teams/${teamId}/members`;
      const res = await apiGet<ListResponse>(url);
      return res.data;
    },
  });
}

/**
 * invalidateRoster is what every membership write has to drop.
 *
 * ["persons"] is the one people forget: the registry answers `is_member`,
 * `teams_label` and — since the leader picker — „who is on this team's
 * soupiska", all of which a membership write changes. Leaving it cached is
 * how a member promoted to člen failed to appear among the candidates for
 * vedoucí.
 */
function invalidateRoster(qc: ReturnType<typeof useQueryClient>, teamId?: ID) {
  if (teamId !== undefined) qc.invalidateQueries({ queryKey: ["teams", teamId, "members"] });
  qc.invalidateQueries({ queryKey: ["teams"] });
  qc.invalidateQueries({ queryKey: ["persons"] });
  // ["me","teams"] is a disjoint prefix from ["teams"], so the line above
  // does not reach it, and useMyTeams is what the dashboard and the sidebar
  // read. A membership write can flip hasRegisteredMember in both directions
  // — promoting a club's only nečlen, or removing its last člen — and until
  // this was here the club kept the old answer about MČR for the 30s
  // staleTime.
  qc.invalidateQueries({ queryKey: ["me", "teams"] });
  // The dashboard's alerts are computed from that same count server-side
  // (service/dashboard: roster_empty), and they are their own query, so they
  // go stale on exactly the same writes.
  qc.invalidateQueries({ queryKey: ["dashboard", "alerts"] });
}

export function useAddTeamMember(teamId: ID) {
  const qc = useQueryClient();
  return useMutation<Membership, ApiError, AddMemberPayload>({
    mutationFn: (payload) => apiPost<Membership>(`/teams/${teamId}/members`, payload),
    onSuccess: () => invalidateRoster(qc, teamId),
    // Errors are rendered inline by the calling form (apiErrorToFormErrors),
    // so the global mutation toast would say the same thing twice.
    meta: { suppressErrorToast: true },
  });
}

export interface AddExistingMemberPayload {
  person_id: ID;
  roles: MembershipRole[];
  kind: MembershipKind;
}

export function useAddExistingTeamMember(teamId: ID) {
  const qc = useQueryClient();
  return useMutation<Membership, ApiError, AddExistingMemberPayload>({
    mutationFn: (payload) =>
      apiPost<Membership>(`/teams/${teamId}/members/existing`, payload),
    onSuccess: () => invalidateRoster(qc, teamId),
    // Errors are rendered inline by the calling form (apiErrorToFormErrors),
    // so the global mutation toast would say the same thing twice.
    meta: { suppressErrorToast: true },
  });
}

// useAddPersonToTeam is the members registry's „Přidat do týmu": the same
// endpoint as useAddExistingTeamMember, with the team picked in the dialog
// rather than known up front. Attaching somebody with no tie to the team
// (a club change) is open to admin / members_admin only.
export function useAddPersonToTeam() {
  const qc = useQueryClient();
  return useMutation<Membership, ApiError, AddExistingMemberPayload & { team_id: ID }>({
    mutationFn: ({ team_id, ...payload }) =>
      apiPost<Membership>(`/teams/${team_id}/members/existing`, payload),
    onSuccess: (_, { team_id }) => invalidateRoster(qc, team_id),
    // The dialog renders the refusal in its error slot.
    meta: { suppressErrorToast: true },
  });
}

export function useUpdatePerson(personId: ID, teamId?: ID) {
  const qc = useQueryClient();
  const url =
    teamId !== undefined
      ? `/teams/${teamId}/persons/${personId}`
      : `/persons/${personId}`;
  return useMutation<Person, ApiError, PersonPayload>({
    mutationFn: (payload) => apiPatch<Person>(url, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["teams"] });
      qc.invalidateQueries({ queryKey: ["persons"] });
    },
    // Errors are rendered inline by the calling form (apiErrorToFormErrors),
    // so the global mutation toast would say the same thing twice.
    meta: { suppressErrorToast: true },
  });
}

export function useUpdateMembership(membershipId: ID) {
  const qc = useQueryClient();
  return useMutation<Membership, ApiError, UpdateMembershipPayload>({
    mutationFn: (payload) => apiPatch<Membership>(`/memberships/${membershipId}`, payload),
    // No teamId here — the route is by membership — but ["teams"] is a prefix
    // of every roster key, so the rosters go too.
    onSuccess: () => invalidateRoster(qc),
    // Errors are rendered inline by the calling form (apiErrorToFormErrors),
    // so the global mutation toast would say the same thing twice.
    meta: { suppressErrorToast: true },
  });
}

export function useRemoveMembership() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, ID>({
    mutationFn: (id) => apiDelete<void>(`/memberships/${id}`),
    // The refusal lists the soutěže that block the removal, and it has to
    // render next to the button that was pressed — see the confirm dialog on
    // TeamDetailPage. A toast saying it again adds nothing.
    meta: { suppressErrorToast: true },
    onSuccess: () => invalidateRoster(qc),
  });
}

export type SeasonPhase =
  | "pre_registration"
  | "registration_open"
  | "registration_closed";

export interface Season {
  id: ID;
  code: string;
  /**
   * The rule set the federation designated for this season — the one that
   * answers „which ladder applies here" when nothing more specific does.
   *
   * NOT `RuleSet.season_id`, which is a descriptive home-season label that
   * several sets can share (every „Duplikovat sadu" copy keeps it). Nil
   * means nobody has decided yet, and it must read that way: borrowing
   * another season's rules to fill the gap is what this field exists to
   * stop.
   */
  default_rule_set_id?: ID | null;
  default_rule_set_name?: string;
  registration_open_from?: string | null;
  registration_open_to?: string | null;
  registration_open: boolean;
  phase: SeasonPhase;
  /** season.FeeDue: the membership fee is owed (from the uzávěrka on). */
  fee_due: boolean;
  active: boolean;
}

export function useSeasons() {
  return useQuery<Season[]>({
    queryKey: ["seasons"],
    queryFn: async () => {
      const res = await apiGet<{ data: Season[] }>("/seasons");
      return res.data;
    },
  });
}

